import {
  pgTable,
  uniqueIndex,
  timestamp,
  integer,
  index,
  boolean,
  varchar,
  jsonb,
  primaryKey,
  uuid,
  bigint,
  text,
} from 'drizzle-orm/pg-core'
import { desc, sql } from 'drizzle-orm'

// auth stuff

export const verificationToken = pgTable(
  'VerificationToken',
  {
    identifier: varchar('identifier', { length: 255 }).primaryKey(),
    token: varchar('token', { length: 255 }).notNull(),
    expires: timestamp('expires', {
      withTimezone: false,
      mode: 'date',
      precision: 3,
    }).notNull(),
  },
  (s) => {
    return {
      tokenIdentifierIndex: uniqueIndex('token_identifier_idx').on(
        s.token,
        s.identifier
      ),
    }
  }
)

export type UserFromClerkPreferences = {
  emailNotifications: boolean
}

export const userFromClerk = pgTable('UserFromClerk', {
  id: varchar('id', { length: 255 }).primaryKey(),
  username: varchar('username', { length: 255 }),
  first_name: varchar('first_name', { length: 255 }),
  last_name: varchar('last_name', { length: 255 }),
  bio: varchar('bio', { length: 255 }),
  profile_image_url: varchar('profile_image_url', { length: 255 }),
  email: varchar('email', { length: 255 }).notNull(),
  last_sign_in_at: timestamp('last_sign_in_at', {
    withTimezone: false,
    mode: 'date',
    precision: 3,
  }),
  created_at: timestamp('created_at', {
    withTimezone: false,
    mode: 'date',
    precision: 3,
  }).notNull(),
  updated_at: timestamp('updated_at', {
    withTimezone: false,
    mode: 'date',
    precision: 3,
  }),
  deleted_at: timestamp('deleted_at', {
    withTimezone: false,
    mode: 'date',
    precision: 3,
  }),
  last_announcement_received: varchar('last_announcement_received', {
    length: 255,
  }),
  preferences: jsonb('preferences')
    .$type<UserFromClerkPreferences>()
    .default({ emailNotifications: true }),
  unsubscribeToken: uuid('unsubscribe_token')
    .default(sql`gen_random_uuid()`)
    .notNull(),
})

export type UserFromClerk = typeof userFromClerk.$inferSelect

// replicache stuff

export const client = pgTable(
  'client',
  {
    id: varchar('id', { length: 255 }).primaryKey(),
    clientgroupid: varchar('clientgroupid', { length: 255 }).notNull(),
    lastmutationid: integer('lastmutationid'),
    lastmodified: timestamp('lastmodified', {
      withTimezone: false,
      mode: 'date',
      precision: 6,
    }),
  },
  (s) => {
    return {
      clientClientgroupidIdx: index('client_clientgroupid_idx').on(
        s.clientgroupid
      ),
    }
  }
)

export type Client = typeof client.$inferSelect

export const clientgroup = pgTable('clientgroup', {
  id: varchar('id', { length: 255 }).primaryKey(),
  clientLastMutationIds: jsonb('clientlastmutationids')
    .notNull()
    .$type<Record<string, number>>(),
  userId: varchar('userid', { length: 255 }).notNull(),
  cvrVersion: integer('cvrversion').notNull(),
  lastmodified: timestamp('lastmodified', {
    withTimezone: false,
    mode: 'date',
    precision: 6,
  }),
})

export type ClientGroup = typeof clientgroup.$inferSelect

export const entry = pgTable(
  'entry',
  {
    key: varchar('key', { length: 255 }).notNull(),
    spaceid: varchar('spaceid', { length: 255 }).notNull(),
    value: jsonb('value').notNull(),
    version: integer('version').notNull(),
    lastmodified: timestamp('lastmodified', {
      withTimezone: false,
      mode: 'date',
      precision: 6,
    }).notNull(),
  },
  (s) => {
    return {
      pk: primaryKey({ columns: [s.spaceid, s.key] }),
      entrySpaceidIdx: index('entry_spaceid_idx').on(s.spaceid),
      entryVersionIdx: index('entry_version_idx').on(s.version),
      entryTeamSlug: index('idx_team_slug')
        .on(sql`(value->>'slug')`)
        .where(sql`key LIKE 'team/%'`),
      entryRepoSlug: index('idx_repo_slug')
        .on(sql`(value->>'slug')`)
        .where(sql`key LIKE 'repository/%'`),
      entryTeamInvites: index('idx_team_invites')
        .using('gin', sql`(value->'invites')`)
        .where(sql`key LIKE 'team/%'`),
      entryRepoGuestInvites: index('idx_repo_guest_invites')
        .using('gin', sql`(value->'guests'->'invites')`)
        .where(sql`key LIKE 'repository/%'`),
      entryRepoKey: index('idx_repo_key')
        .on(s.key)
        .where(sql`key LIKE 'repository/%'`),
      entryBlockAndBRsSourceBlockId: index('idx_block_and_brs_source_block_id')
        .on(sql`(value->>'sourceBlockId')`)
        .where(sql`key LIKE 'block/%' OR key LIKE 'br/%'`),
      entryBlockAndBRsBlockId: index('idx_brs_block_id')
        .on(sql`(value->>'blockId')`)
        .where(sql`key LIKE 'br/%'`),
      entryBlockAndBRsParentId: index('idx_brs_parent_id')
        .on(sql`(value->>'parentId')`)
        .where(sql`key LIKE 'br/%'`),
      entryBRStructural: index('idx_br_structural')
        .on(sql`((value->>'structural')::boolean)`)
        .where(sql`key LIKE 'br/%'`),
      entryBRValidFrom: index('idx_br_valid_from')
        .on(sql`jsonb_to_timestamptz_immutable(value, 'validFrom')`)
        .where(sql`key LIKE 'br/%'`),
      entryBRValidTo: index('idx_br_valid_to')
        .on(sql`jsonb_to_timestamptz_immutable(value, 'validTo')`)
        .where(sql`key LIKE 'br/%'`),
      entryBlockRootCreatedAt: index('idx_block_root_created_at')
        .on(sql`jsonb_to_timestamptz_immutable(value, 'createdAt')`)
        .where(sql`key LIKE 'block/%' AND value->>'type' = 'root'`),
      entryBRBranchId: index('idx_br_branch_id')
        .on(sql`(value->>'branchId')`)
        .where(sql`key LIKE 'br/%'`),
      entryBRDiscardedAt: index('idx_br_discarded_at')
        .on(sql`jsonb_to_timestamptz_immutable(value, 'discardedAt')`)
        .where(sql`key LIKE 'br/%'`),
      entryBRIsCommitted: index('idx_br_is_committed')
        .on(sql`((value->>'isCommitted')::boolean)`)
        .where(sql`key LIKE 'br/%'`),
      entryEventCreatedAt: index('idx_event_created_at')
        .on(sql`jsonb_to_timestamptz_immutable(value, 'createdAt')`)
        .where(sql`key LIKE 'event/%'`),
      entryClientLastActiveAt: index('idx_client_last_active_at')
        .on(sql`(value->>'lastActiveAt')::bigint`)
        .where(sql`key LIKE 'client/%'`),

      entryBRStructuralOptimized: index('idx_br_structural_optimized')
        .on(
          sql`(value->>'branchId'),
          spaceid,
          ((value->>'isCommitted')::boolean),
          jsonb_to_timestamptz_immutable(value, 'validFrom'),
          jsonb_to_timestamptz_immutable(value, 'validTo'),
          (value->>'sourceBlockId'),
          (value->>'blockId'),
          key,
          value,
          version,
          lastmodified`
        )
        .where(
          sql`key LIKE 'br/%' AND value->>'discardedAt' IS NULL AND value->>'structural' = 'true'`
        ),

      entryBRNonStructuralOptimized: index('idx_br_non_structural_optimized')
        .on(
          sql`spaceid,
          (value->>'branchId'),
          ((value->>'isCommitted')::boolean),
          jsonb_to_timestamptz_immutable(value, 'validFrom'),
          jsonb_to_timestamptz_immutable(value, 'validTo')`
        )
        .where(sql`key LIKE 'br/%' AND value->>'discardedAt' IS NULL`),

      entryBRSourceBlockLookup: index('idx_br_source_block_lookup')
        .on(
          sql`spaceid,
          (value->>'sourceBlockId'),
          (value->>'branchId'),
          ((value->>'isCommitted')::boolean),
          jsonb_to_timestamptz_immutable(value, 'validFrom'),
          jsonb_to_timestamptz_immutable(value, 'validTo')`
        )
        .where(sql`key LIKE 'br/%' AND value->>'discardedAt' IS NULL`),

      entryBRParentIdLookup: index('idx_br_parent_id_lookup')
        .on(
          sql`spaceid,
          (value->>'parentId'),
          (value->>'branchId'),
          ((value->>'isCommitted')::boolean),
          jsonb_to_timestamptz_immutable(value, 'validFrom'),
          jsonb_to_timestamptz_immutable(value, 'validTo')`
        )
        .where(sql`key LIKE 'br/%' AND value->>'discardedAt' IS NULL`),

      // Index for instance/component queries
      entryBlockInstanceTarget: index('idx_block_instance_target')
        .on(sql`(value->'typeOptions'->>'targetBlockId')`)
        .where(sql`key LIKE 'block/%' AND value->>'type' = 'instance'`),
      // Index for reference queries
      entryBlockReferenceTargets: index('idx_block_reference_targets')
        .using('gin', sql`(value->'value'->'targetBlockIds') jsonb_path_ops`)
        .where(sql`key LIKE 'block/%'`),
      // Compound index for BR queries by blockId and sourceBlockId
      entryBRBlockAndSourceId: index('idx_br_block_and_source_id')
        .on(sql`(value->>'blockId'), (value->>'sourceBlockId')`)
        .where(sql`key LIKE 'br/%'`),
    }
  }
)

export type Entry = typeof entry.$inferSelect

export const cvrNormie = pgTable(
  'cvr_normie',
  {
    key: varchar('key', { length: 255 }).notNull(),
    entryKey: varchar('entrykey', { length: 255 }).notNull(),
    version: integer('version').notNull(),
    lastmodified: timestamp('lastmodified', {
      withTimezone: false,
      mode: 'date',
      precision: 6,
    }).notNull(),
    syncSequence: bigint('syncsequence', {
      mode: 'bigint',
    }),
    replicacheSchemaVersion: varchar('replicache_schema_version', {
      length: 255,
    }),
  },
  (s) => {
    return {
      // Primary index - supports the unique constraint and JOIN condition
      cvrNormieKeyEntryKeyIdx: uniqueIndex('cvr_normie_key_entry_key_idx').on(
        s.key,
        s.entryKey
      ),

      // support sync sequence lookups
      // This ordering allows efficient range scans on syncsequence while still
      // utilizing the key prefix
      cvrNormieKeySyncIdx: index('cvr_normie_key_sync_idx').on(
        s.key,
        s.syncSequence
      ),

      // support version comparisons
      cvrNormieKeyVersionIdx: index('cvr_normie_key_version_idx').on(
        s.key,
        s.version
      ),
    }
  }
)

export type CvrNormie = typeof cvrNormie.$inferSelect

export const meta = pgTable('meta', {
  key: varchar('key', { length: 255 }).primaryKey(),
  value: jsonb('value').notNull(),
})

export const space = pgTable('space', {
  id: varchar('id', { length: 255 }).primaryKey(),
  lastmodified: timestamp('lastmodified', {
    withTimezone: false,
    mode: 'date',
    precision: 6,
  }).notNull(),
})

// misc

export const waitlistUser = pgTable(
  'WaitlistUser',
  {
    id: varchar('id', { length: 255 }).primaryKey(),
    email: varchar('email', { length: 255 }).notNull(),
    twitter: varchar('twitter', { length: 255 }),
    hasAccess: boolean('hasAccess').notNull(),
    createdAt: timestamp('createdAt', {
      withTimezone: false,
      mode: 'date',
      precision: 3,
    }).notNull(),
  },
  (s) => {
    return {
      waitlistUserEmailIdx: uniqueIndex('WaitlistUser_email_key').on(s.email),
    }
  }
)

export const cachedAPIQueries = pgTable(
  'CachedAPIQuery',
  {
    token: varchar('token', { length: 255 }).notNull(),
    branchId: varchar('branchId', { length: 255 }).notNull(),
    tag: varchar('tag', { length: 255 }).notNull(),
    sdkBuildId: varchar('sdkBuildId', { length: 255 })
      .notNull()
      .default('old_version'),
    apiVersion: varchar('apiVersion', { length: 255 }).notNull(),
    ref: varchar('ref', { length: 255 }),
    responseHash: varchar('responseHash', { length: 255 }).notNull(),
    queryBody: jsonb('body')
      .notNull()
      .$type<{ query: string; variables: Record<string, string> }>(),
    createdAt: timestamp('createdAt', {
      withTimezone: false,
      mode: 'date',
      precision: 3,
    }).notNull(),
  },
  (s) => {
    return {
      pk: primaryKey({ columns: [s.branchId, s.sdkBuildId, s.tag] }),
      cachedApiQueriesBranchSdkCreatedAtIdx: index(
        'cached_api_queries_branch_sdk_created_at_idx'
      ).on(s.branchId, s.sdkBuildId, s.createdAt),
      cachedApiQueriesBranchCreatedAtIdx: index(
        'cached_api_queries_branch_created_at_idx'
      ).on(s.branchId, s.createdAt),
    }
  }
)

export const analyticsEvents = pgTable(
  'analytics_events',
  {
    id: varchar('id', { length: 255 }).primaryKey(),
    timestamp: timestamp('timestamp', {
      withTimezone: false,
      mode: 'date',
      precision: 3,
    }).notNull(),
    ip: varchar('ip', { length: 255 }),
    country: varchar('country', { length: 255 }),
    city: varchar('city', { length: 255 }),
    region: varchar('region', { length: 255 }),
    latitude: varchar('latitude', { length: 255 }),
    longitude: varchar('longitude', { length: 255 }),
    device: varchar('device', { length: 255 }),
    deviceVendor: varchar('device_vendor', { length: 255 }),
    deviceModel: varchar('device_model', { length: 255 }),
    browser: varchar('browser', { length: 255 }),
    browserVersion: varchar('browser_version', { length: 255 }),
    engine: varchar('engine', { length: 255 }),
    engineVersion: varchar('engine_version', { length: 255 }),
    os: varchar('os', { length: 255 }),
    osVersion: varchar('os_version', { length: 255 }),
    cpuArchitecture: varchar('cpu_architecture', { length: 255 }),
    ua: text('ua'),
    bot: boolean('bot'),
    referrer: text('referrer'),
    referrerUrl: text('referrer_url'),
    repoId: varchar('repo_id', { length: 255 }),
    blockId: varchar('block_id', { length: 255 }),
    value: jsonb('value'),
    eventName: varchar('event_name', { length: 255 }),
  },
  (s) => {
    return {
      /** Core slice-first index: (block_id, repo_id) */
      blockRepoIdx: index('analytics_events_block_repo_idx').on(
        s.blockId,
        s.repoId
      ),

      /** Broad JSONB GIN index for arbitrary key filters */
      valueGinIdx: index('analytics_events_value_gin_idx').using(
        'gin',
        s.value.op('jsonb_path_ops')
      ),

      /** Optional ordering index: (block_id, repo_id, timestamp DESC) */
      blockRepoTsIdx: index('analytics_events_block_repo_ts_idx').on(
        s.blockId,
        s.repoId,
        desc(s.timestamp)
      ),
    }
  }
)

export const analyticsEventSubscribers = pgTable(
  'analytics_event_subscribers',
  {
    id: varchar('id', { length: 255 }).primaryKey(),
    repoId: varchar('repo_id', { length: 255 }),
    blockId: varchar('block_id', { length: 255 }),
    commitId: varchar('commit_id', { length: 255 }),
    branch: varchar('branch', { length: 255 }),
    data: jsonb('data'),
    createdAt: timestamp('created_at', {
      withTimezone: false,
      mode: 'date',
      precision: 6,
    }),
  },
  (s) => {
    return {
      repoBlockCommitBranchCreatedAtIdx: index(
        'event_subscribers_repo_block_commit_branch_created_at_idx'
      ).on(s.repoId, s.blockId, s.commitId, s.branch, s.createdAt),
    }
  }
)

export type AnalyticsEventSubscriber =
  typeof analyticsEventSubscribers.$inferSelect

export const mcpTokens = pgTable(
  'mcp_tokens',
  {
    id: text('id').primaryKey(),
    sessionToken: text('session_token').notNull(),
    userId: text('user_id').notNull(),
    repoId: text('repo_id').notNull(),
  },
  (s) => ({
    userRepoIdx: index('user_repo_idx').on(s.userId, s.repoId),
  })
)

export type MCPToken = typeof mcpTokens.$inferSelect
export const MCP_TOKEN_PREFIX = 'bshb_mcp_'
